import styles from "./Text.module.css";

/**
 * Typography aligned to Figma text samples (see `src/styles/tokens.css` header for node links).
 *
 * @typedef {'headlineLg' | 'headlineMd' | 'titleMd' | 'titleSm' | 'paragraphLg' | 'paragraphMd' | 'paragraphSm' | 'tagSmall'} TextVariant
 * - headlineLg — Headline/Large (PP Agrandir 70 / 500 / lh 1 / −0.7px), node 299:2982
 * - headlineMd — Headline/Medium (54px, −0.54px), node 375:4445
 * - titleMd — Title/Medium (28px, capitalize, default center), node 375:4446
 * - titleSm — Title/Small (22px, capitalize, default center), node 348:4880
 * - paragraphLg — Paragraph/Large (Inter 18 semibold, −0.36px), node 299:2985
 * - paragraphMd — Paragraph/Medium (Inter 14 medium, −0.28px), node 299:2995
 * - paragraphSm — Paragraph/Small (Inter 12 medium, lh 1.1, −0.24px), node 299:2987
 * - tagSmall — Tag/Small (Inter 12 bold, uppercase, +0.96px, default center), node 299:2996
 */

const VARIANT_CLASS = {
  headlineLg: styles.headlineLg,
  headlineMd: styles.headlineMd,
  titleMd: styles.titleMd,
  titleSm: styles.titleSm,
  paragraphLg: styles.paragraphLg,
  paragraphMd: styles.paragraphMd,
  paragraphSm: styles.paragraphSm,
  tagSmall: styles.tagSmall,
};

const CENTER_DEFAULT = new Set(["titleMd", "titleSm", "tagSmall"]);

const ALIGN_CLASS = {
  start: styles.alignStart,
  center: styles.alignCenter,
  end: styles.alignEnd,
};

function resolveAlign(alignProp, variant) {
  if (alignProp) return alignProp;
  return CENTER_DEFAULT.has(variant) ? "center" : "start";
}

export default function Text({
  variant = "paragraphMd",
  as: Component = "p",
  align: alignProp,
  color,
  className,
  children,
  ...rest
}) {
  const v = VARIANT_CLASS[variant] ?? styles.paragraphMd;
  const align = resolveAlign(alignProp, variant);
  const alignCls = ALIGN_CLASS[align] ?? styles.alignStart;

  const heading =
    Component === "h1" ||
    Component === "h2" ||
    Component === "h3" ||
    Component === "h4" ||
    Component === "h5" ||
    Component === "h6";

  const rootClass = [v, alignCls, heading && styles.asHeading, className]
    .filter(Boolean)
    .join(" ");

  const style = color ? { "--text-color": color } : undefined;

  return (
    <Component className={rootClass} style={style} {...rest}>
      {children}
    </Component>
  );
}
